import React from 'react';
import { Box, Heading, Text, Button, VStack, HStack, Icon, useColorModeValue, Alert, AlertIcon } from '@chakra-ui/react';
import { FiAlertTriangle, FiMail, FiArrowLeft } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { isOwnerHost, ownerPath } from '../../utils/portalHost';

/**
 * ErrorPage - Page d'erreur spécialement conçue pour les guests
 * Gère les cas où les guests modifient l'URL et cassent l'accès au guestbook
 */
const ErrorPage = ({ code, title, message, backPath }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const subtle = useColorModeValue('gray.600', 'gray.300');

  // Permet de passer des données via location.state si souhaité
  const state = location.state || {};
  const effectiveCode = state.code || code || t('error.oops', 'Oups');
  const effectiveTitle = state.title || title || t('error.unauthorized', 'Accès non autorisé');
  const effectiveMessage = state.message || message || t('error.urlModified', 'Il semble que vous ayez modifié l\'URL ou que le lien ne soit plus valide.');

  // Récupérer l'URL originale depuis le localStorage ou les paramètres
  const originalUrl = state.originalUrl || localStorage.getItem('originalGuestbookUrl');

  // Détermine si c'est un guest, un owner, ou un utilisateur admin connecté
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const ownerToken = typeof window !== 'undefined' ? localStorage.getItem('owner-token') : null;
  // Sur le domaine owner dédié, TOUT chemin est owner-portal (URLs courtes sans /owner/)
  const isOwnerPortal = isOwnerHost() || location.pathname.includes('/owner/');
  const isGuest = !token && !ownerToken;

  // Extraire le companySlug de l'URL pour le portail owner
  const getCompanySlug = () => {
    const match = location.pathname.match(/^\/([^/]+)\/owner/)
      || (isOwnerHost() ? location.pathname.match(/^\/([^/]+)/) : null);
    return match ? match[1] : null;
  };

  // Pour les guests, on essaie de les rediriger vers l'URL originale
  const handleReturnToOriginal = () => {
    if (originalUrl) {
      window.location.href = originalUrl;
    } else {
      // Si pas d'URL originale, on revient en arrière
      navigate(-1);
    }
  };

  // Pour les owners, retour à l'accueil du portail owner
  const handleReturnToOwnerPortal = () => {
    const companySlug = getCompanySlug();
    if (companySlug) {
      navigate(ownerPath(companySlug, 'home'));
    } else {
      navigate(-1);
    }
  };

  // Pour les utilisateurs admin connectés, retour au dashboard
  const handleReturnToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <Box minH="70vh" display="flex" alignItems="center" justifyContent="center" px={4} position="relative">
      {/* Language Switcher en haut à droite */}
      <Box position="absolute" top={4} right={4}>
        <LanguageSwitcher />
      </Box>
      
      <Box bg={bg} border="1px" borderColor={border} borderRadius="2xl" p={{ base: 6, md: 10 }} maxW="lg" w="full" textAlign="center" boxShadow="lg">
        <VStack spacing={6}>
          <Icon as={FiAlertTriangle} w={12} h={12} color="orange.400" />
          
          <VStack spacing={2}>
            <Heading size="lg" color="red.500">{effectiveCode}</Heading>
            <Heading size="md">{effectiveTitle}</Heading>
          </VStack>

          <Text color={subtle} fontSize="md" lineHeight="tall">
            {effectiveMessage}
          </Text>

          {isGuest && (
            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              <Text fontSize="sm">
                {t('error.useOriginalLink', 'Veuillez utiliser le lien qui vous a été envoyé par email ou SMS pour accéder à votre livret d\'accueil.')}
              </Text>
            </Alert>
          )}

          <VStack spacing={3} w="full">
            {isOwnerPortal ? (
              // Owner portal - redirect to owner home
              <Button
                leftIcon={<FiArrowLeft />}
                colorScheme="blue"
                size="lg"
                w="full"
                borderRadius="xl"
                onClick={handleReturnToOwnerPortal}
              >
                {t('error.backToPortal', 'Retour à l\'accueil')}
              </Button>
            ) : isGuest ? (
              // Guest - redirect to original URL or back
              <>
                {originalUrl && (
                  <Button
                    leftIcon={<FiMail />}
                    colorScheme="blue"
                    size="lg"
                    w="full"
                    borderRadius="xl"
                    onClick={handleReturnToOriginal}
                  >
                    {t('error.returnToOriginal', 'Retourner au lien original')}
                  </Button>
                )}
                <Button
                  leftIcon={<FiArrowLeft />}
                  variant="outline"
                  size="md"
                  w="full"
                  borderRadius="xl"
                  onClick={handleReturnToOriginal}
                >
                  {t('error.back', 'Retour')}
                </Button>
              </>
            ) : (
              // Admin user - redirect to dashboard
              <Button
                leftIcon={<FiArrowLeft />}
                colorScheme="blue"
                size="lg"
                w="full"
                borderRadius="xl"
                onClick={handleReturnToDashboard}
              >
                {t('error.backToDashboard', 'Retour au dashboard')}
              </Button>
            )}
          </VStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default ErrorPage;
